import type { Readable, Writable } from "node:stream";
import {
  COMPANION_BRIDGE_LINE_MAX_LENGTH,
  parseRequestLine,
  serializeEvent,
  type CompanionBridgeEvent,
  type CompanionBridgeRequest,
} from "@m8t-stack/companion-bridge-contract";
import { getFoundryToken } from "./auth.js";
import {
  chatCompanionMessage,
  converseCompanionMessage,
  decideCompanionMessage,
  fetchAgents,
  historyCompanionMessages,
  recallCompanionMessages,
  resolveConversationId,
  rosterCompanions,
  sendCompanionMessage,
  type CompanionChatDeps,
} from "./companion-chat-client.js";
import { checkCompanionUpdate } from "./companion-update-check.js";
import { defaultLocalCompanionInstallOptions } from "../commands/companion/install.js";

type EventSink = (event: CompanionBridgeEvent) => void;
type TerminalEvent = CompanionBridgeEvent;

/** Well under the token's own lifetime, so a cached bearer stays live. */
const TOKEN_TTL_MS = 10 * 60_000;
/** Rosters and conversation bindings move rarely; this bounds a stale hit. */
const CONTEXT_TTL_MS = 5 * 60_000;

/**
 * The failure reasons a stale cache entry can present as: an expired or
 * revoked token (not-authorized), a conversation the gateway no longer knows
 * (not-connected covers its 404), or an agent renamed out from under the
 * cached roster (mate-unavailable). `busy` is a live answer and `bad-request`
 * is the caller's own error; neither is retried.
 */
const STALE_CONTEXT_REASONS: ReadonlySet<string> = new Set([
  "not-authorized",
  "not-connected",
  "mate-unavailable",
]);

export interface CompanionBridgeServeDeps {
  /** Base chat dependencies the caching layer wraps; tests count calls here. */
  readonly chat?: CompanionChatDeps;
  readonly update?: () => Promise<TerminalEvent>;
  readonly now?: () => number;
}

interface CachedValue<T> {
  readonly value: T;
  readonly at: number;
}

/**
 * The authenticated context a bridge session holds between requests, in
 * process memory only — nothing here is ever written anywhere. The token is
 * the reason this command runs one process per app rather than one per
 * machine: it lives exactly as long as its process.
 */
class ServeContext {
  private token: CachedValue<string> | null = null;
  private readonly agents = new Map<
    string,
    CachedValue<readonly Record<string, unknown>[]>
  >();
  private readonly conversations = new Map<string, CachedValue<string>>();
  private cacheServed = false;

  constructor(
    private readonly now: () => number,
    private readonly base: CompanionChatDeps,
  ) {}

  /** Starts a request (or a retry) with a clean served-from-cache slate. */
  beginRequest(): void {
    this.cacheServed = false;
  }

  /** Whether the request in flight consumed anything cached. */
  servedFromCache(): boolean {
    return this.cacheServed;
  }

  /** Drops every held value so the next resolution starts from nothing. */
  invalidate(): void {
    this.token = null;
    this.agents.clear();
    this.conversations.clear();
  }

  private fresh<T>(
    entry: CachedValue<T> | null | undefined,
    ttl: number,
  ): entry is CachedValue<T> {
    return entry !== null && entry !== undefined && this.now() - entry.at < ttl;
  }

  /** The chat dependencies with the caching wrappers layered in. */
  chatDeps(): CompanionChatDeps {
    return {
      ...this.base,
      getFoundryToken: async () => {
        if (this.fresh(this.token, TOKEN_TTL_MS)) {
          this.cacheServed = true;
          return this.token.value;
        }
        const value = await (this.base.getFoundryToken ?? getFoundryToken)();
        this.token = { value, at: this.now() };
        return value;
      },
      fetchAgents: async (fetchImpl, origin, token, signal) => {
        const held = this.agents.get(origin);
        if (this.fresh(held, CONTEXT_TTL_MS)) {
          this.cacheServed = true;
          return held.value;
        }
        const value = await (this.base.fetchAgents ?? fetchAgents)(
          fetchImpl,
          origin,
          token,
          signal,
        );
        this.agents.set(origin, { value, at: this.now() });
        return value;
      },
      resolveConversationId: async (fetchImpl, origin, headers, agentName, signal) => {
        const key = `${origin} ${agentName}`;
        const held = this.conversations.get(key);
        if (this.fresh(held, CONTEXT_TTL_MS)) {
          this.cacheServed = true;
          return held.value;
        }
        const value = await (this.base.resolveConversationId ?? resolveConversationId)(
          fetchImpl,
          origin,
          headers,
          agentName,
          signal,
        );
        this.conversations.set(key, { value, at: this.now() });
        return value;
      },
    };
  }
}

/**
 * NDJSON requests as they arrive, one at a time. The generator only resumes
 * reading once the caller finishes the previous request, which is what makes
 * the session serial. A line that outgrows the wire bound before its newline
 * arrives can never become parseable, so it ends the read rather than the
 * buffer.
 */
async function* requestLines(stdin: Readable): AsyncGenerator<string> {
  // Decoded as a stream: a chunk boundary inside a multi-byte character must
  // not corrupt the line, because one corrupt line ends the whole session.
  stdin.setEncoding("utf8");
  let buffered = "";
  for await (const chunk of stdin) {
    buffered += String(chunk);
    for (;;) {
      const breakAt = buffered.indexOf("\n");
      if (breakAt === -1) break;
      const line = buffered.slice(0, breakAt).replace(/\r$/u, "");
      buffered = buffered.slice(breakAt + 1);
      yield line;
    }
    // One more than the bound, because a trailing \r is still framable.
    if (buffered.length > COMPANION_BRIDGE_LINE_MAX_LENGTH + 1) {
      throw new Error("request exceeds bridge bound");
    }
  }
  if (buffered.length > 0) yield buffered.replace(/\r$/u, "");
}

interface ServeHandlers {
  readonly roster: (sink: EventSink) => Promise<TerminalEvent>;
  readonly update: () => Promise<TerminalEvent>;
  readonly send: (
    request: Extract<CompanionBridgeRequest, { type: "send" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly converse: (
    request: Extract<CompanionBridgeRequest, { type: "converse" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly chat: (
    request: Extract<CompanionBridgeRequest, { type: "chat" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly decide: (
    request: Extract<CompanionBridgeRequest, { type: "decide" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly history: (
    request: Extract<CompanionBridgeRequest, { type: "history" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly recall: (
    request: Extract<CompanionBridgeRequest, { type: "recall" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
}

async function dispatchRequest(
  request: CompanionBridgeRequest,
  sink: EventSink,
  handlers: ServeHandlers,
): Promise<TerminalEvent> {
  if (request.type === "roster") return handlers.roster(sink);
  if (request.type === "update") {
    const terminal = await handlers.update();
    sink(terminal);
    return terminal;
  }
  if (request.type === "history") return handlers.history(request, sink);
  if (request.type === "recall") return handlers.recall(request, sink);
  if (request.type === "converse") return handlers.converse(request, sink);
  if (request.type === "chat") return handlers.chat(request, sink);
  if (request.type === "decide") return handlers.decide(request, sink);
  return handlers.send(request, sink);
}

/**
 * Runs one request against the cached context, re-resolving once when a
 * cached value fails on use. Only a pre-dispatch failure is retried — it is
 * the one shape that proves nothing reached the gateway, and it always
 * arrives as a lone `failed` event, so holding just that event back keeps
 * every other event streaming out the moment it happens. The retry runs with
 * everything invalidated; whatever it reports is the honest answer.
 */
async function handleWithContext(
  request: CompanionBridgeRequest,
  sink: EventSink,
  handlers: ServeHandlers,
  context: ServeContext,
): Promise<void> {
  context.beginRequest();
  const held = { failure: null as CompanionBridgeEvent | null, streamed: false };
  const firstAttempt: EventSink = (event) => {
    if (
      !held.streamed &&
      held.failure === null &&
      event.type === "failed" &&
      STALE_CONTEXT_REASONS.has(event.reason) &&
      context.servedFromCache()
    ) {
      held.failure = event;
      return;
    }
    held.streamed = true;
    if (held.failure !== null) {
      // More events after a failure is not a shape the handlers produce, but
      // if it happens the failure must not be swallowed.
      sink(held.failure);
      held.failure = null;
    }
    sink(event);
  };

  const terminal = await dispatchRequest(request, firstAttempt, handlers);
  if (held.failure === null) {
    // An exchange that dispatched and then went indeterminate may have died
    // on a revoked token or a dropped binding — outcomes a post-dispatch
    // failure cannot name. It is never retried, but the context is dropped so
    // the next request resolves fresh instead of repeating the fate.
    if (terminal.type === "indeterminate" && context.servedFromCache()) {
      context.invalidate();
    }
    return;
  }

  context.invalidate();
  context.beginRequest();
  await dispatchRequest(request, sink, handlers);
}

/**
 * The serving bridge: the same wire contract as the one-shot command, looped.
 * Requests are handled strictly one at a time; stdin closing is the exit
 * condition; a malformed line ends the whole session exactly as strictly as
 * the one-shot rejects it, and the caller's respawn is the recovery. Nothing
 * read or cached here survives the process.
 */
export async function runCompanionBridgeServe(
  stdin: Readable,
  stdout: Writable,
  stderr: Writable,
  deps: CompanionBridgeServeDeps = {},
): Promise<number> {
  const context = new ServeContext(deps.now ?? Date.now, deps.chat ?? {});
  const chat = context.chatDeps();
  const handlers: ServeHandlers = {
    roster: (sink) => rosterCompanions(sink, chat),
    send: (request, sink) => sendCompanionMessage(request, sink, chat),
    converse: (request, sink) => converseCompanionMessage(request, sink, chat),
    chat: (request, sink) => chatCompanionMessage(request, sink, chat),
    decide: (request, sink) => decideCompanionMessage(request, sink, chat),
    history: (request, sink) => historyCompanionMessages(request, sink, chat),
    recall: (request, sink) => recallCompanionMessages(request, sink, chat),
    update:
      deps.update ?? (() => checkCompanionUpdate(defaultLocalCompanionInstallOptions())),
  };
  const sink: EventSink = (event) => {
    stdout.write(`${serializeEvent(event)}\n`);
  };

  try {
    for await (const raw of requestLines(stdin)) {
      let request: CompanionBridgeRequest;
      try {
        request = parseRequestLine(raw);
      } catch {
        stderr.write("bridge request rejected\n");
        return 2;
      }
      try {
        await handleWithContext(request, sink, handlers, context);
      } catch {
        stderr.write("bridge request failed\n");
        return 3;
      }
    }
  } catch {
    stderr.write("bridge request rejected\n");
    return 2;
  }
  return 0;
}
